/**
 * The one accent colour the video is built around.
 *
 * Three things carry it — the character's accent, the name chips above each
 * card, and the box around the word being spoken — and they read as a single
 * palette only because they are literally the same value. So it lives here once
 * and everything reads it from the scene.
 *
 * The character art is the awkward one: its accent is baked into the SVG
 * source. Rather than ship a copy of every pose per colour, the source is
 * recoloured in place and handed to the renderer as a data URL. That works
 * because the accent only ever appears as a `fill` or `stroke` value in those
 * files — never inside an id, gradient name or path — so a plain substitution
 * cannot corrupt the artwork.
 */

/** The accent as authored in `public/characters/**.svg`. */
const SOURCE_ACCENT = /#4d2ef5/gi;

export const DEFAULT_THEME_COLOR = "#4d2ef5";

export const THEME_SWATCHES = [
	DEFAULT_THEME_COLOR,
	"#fd8c00",
	"#cd0e66",
	"#22ab24",
];

/** Recoloured pose art, keyed by source and colour — the art never changes. */
const recoloured = new Map<string, Promise<string>>();
const sources = new Map<string, Promise<string>>();

/**
 * Enough for a few characters' worth of poses in a handful of colours.
 *
 * A cap rather than an open map because the colour picker fires on every frame
 * of a drag: without one, sweeping the spectrum banks a data URL per shade for
 * the life of the page.
 */
const MAX_RECOLOURED = 48;

/**
 * A data URL for `url`'s artwork with the accent replaced by `color`.
 *
 * A data URL rather than an object URL on purpose: there is no lifetime to
 * manage, so a revoke can never race the canvas and leave a blank character in
 * an export.
 */
export function recolouredSvgUrl({
	url,
	color,
}: {
	url: string;
	color: string;
}): Promise<string> {
	const accent = normalizeHex({ color });
	if (accent.toLowerCase() === DEFAULT_THEME_COLOR) return Promise.resolve(url);

	const key = `${url}|${accent}`;
	const cached = recoloured.get(key);
	if (cached) return cached;

	const pending = loadSource({ url }).then(
		(svg) =>
			`data:image/svg+xml;charset=utf-8,${encodeURIComponent(
				svg.replace(SOURCE_ACCENT, accent),
			)}`,
	);

	// Map iterates in insertion order, so the first key is the oldest.
	if (recoloured.size >= MAX_RECOLOURED) {
		const oldest = recoloured.keys().next().value;
		if (oldest !== undefined) recoloured.delete(oldest);
	}

	recoloured.set(key, pending);
	// A failed fetch must not be cached, or the pose stays broken for the session.
	void pending.catch(() => recoloured.delete(key));
	return pending;
}

function loadSource({ url }: { url: string }): Promise<string> {
	const cached = sources.get(url);
	if (cached) return cached;

	const pending = fetch(url).then((response) => {
		if (!response.ok) throw new Error(`Could not load ${url}`);
		return response.text();
	});

	sources.set(url, pending);
	void pending.catch(() => sources.delete(url));
	return pending;
}

/**
 * Black or white, whichever is readable on `color`.
 *
 * The picker accepts any colour, so the chip and highlight text cannot assume
 * white — a yellow accent would swallow it. The crossover is the luminance at
 * which black and white contrast equally (WCAG), so this always picks the
 * better of the two rather than guessing at a brightness cutoff.
 */
export function readableTextOn({ color }: { color: string }): string {
	const hex = normalizeHex({ color }).slice(1);
	const channel = ({ at }: { at: number }) => {
		const value = parseInt(hex.slice(at, at + 2), 16) / 255;
		return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
	};

	const luminance =
		0.2126 * channel({ at: 0 }) +
		0.7152 * channel({ at: 2 }) +
		0.0722 * channel({ at: 4 });

	return luminance > 0.179 ? "#0f172a" : "#ffffff";
}

/** Six-digit lowercase hex, or the default if the value isn't a colour. */
export function normalizeHex({ color }: { color: string }): string {
	const value = color.trim().toLowerCase();
	if (/^#[0-9a-f]{6}$/.test(value)) return value;
	if (/^#[0-9a-f]{3}$/.test(value)) {
		return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
	}
	return DEFAULT_THEME_COLOR;
}
